import { ElInput, ElSelect, type InputInstance } from 'element-plus'
import { h, unref, withDirectives, type Ref, type FunctionalComponent } from 'vue'
import type { ManagedRow } from '@/composables/table-row-inline-edit'
import { Icon } from '@iconify/vue'
import { ElButton, ElCheckbox, type CheckboxValueType } from 'element-plus'
import type { CellRenderer, HeaderCellRenderer } from 'element-plus/es/components/table-v2/src/types.mjs'
import { vMaska } from 'maska/vue'

export type InputCellProps = {
  value: string
  intermediate?: boolean
  onChange: (value: string) => void
  forwardRef: (el: InputInstance) => void
}

export type SelectCellProps = InputCellProps & {
  options: any
  valueKey?: string
  filterable?: boolean
  clearable?: boolean
  valueOnClear?: any
}

export type TextInputCellProps = InputCellProps & {
  mask?: string
  maskTokens?: string
}

/**
 * Functional component to render an ElInput cell.
 *
 * @param {InputCellProps} param0
 */
export const InputCell: FunctionalComponent<InputCellProps> = ({
  value,
  onChange,
  forwardRef,
  mask,
  maskTokens,
}: TextInputCellProps) => {
  return withDirectives(
    h(ElInput, {
      ref: forwardRef as any,
      onInput: onChange,
      modelValue: value,
      clearable: true,
      'data-maska': mask,
      'data-maska-tokens': maskTokens,
    }),
    [[vMaska]]
  )
}

/**
 * Function component to render an ElSelect cell.
 *
 * @param {SelectCellProps} param0
 */
export const SelectCell: FunctionalComponent<SelectCellProps> = ({
  value,
  valueKey,
  onChange,
  forwardRef,
  options,
  filterable = true,
  clearable = false,
  valueOnClear,
}: SelectCellProps) => {
  return h(
    ElSelect,
    {
      ref: forwardRef as any,
      onChange,
      modelValue: value,
      filterable,
      clearable,
      valueKey,
      valueOnClear,
    },
    options
  )
}

/********************/
/* Action icon cell */
/********************/

/**
 * Receives an icon id and an action and returns a cell renderer (itself a function)
 * @param icon an iconify id _(string)_ or a dict with 'display' and 'editing' keys with icons for those two states.
 * @param action action to trigger on icon click
 * @param rowBeingEdited _(optional)_ inline editing buffer to determine the row's state. Required for status icons to work.
 */
export const actionCellRendererGenerator: <T extends ManagedRow<unknown>>(
  icon: string | Record<'display' | 'editing', string>,
  action: (rowData: T) => void,
  rowBeingEdited?: Ref<ManagedRow<unknown> | undefined>,
  iconTitle?: string
) => CellRenderer<T> =
  (icon, action, rowBeingEdited, iconTitle) =>
  ({ rowData }) => {
    let resolvedIcon
    // If we have an edition buffer and an icon dictionary, use that
    if (rowBeingEdited?.value && !(typeof icon === 'string')) {
      const editing = rowBeingEdited.value?.id && rowBeingEdited.value.id == rowData.id
      resolvedIcon = editing ? icon.editing : icon.display
    } else {
      // Else, use the icon if it's string or the display variant if it's dict
      resolvedIcon = typeof icon === 'string' ? icon : icon.display
    }

    return h(
      ElButton,
      {
        title: iconTitle ?? '',
        link: true,
        onClick: () => {
          action(rowData)
        },
      },
      () => [h(Icon, { icon: resolvedIcon, height: '16' })]
    )
  }

/************************/
/* Table selection cell */
/************************/

export const selectionCellRendererGenerator: <T>(onSelect?: () => void) => CellRenderer<ManagedRow<T>> =
  (onSelect) =>
  ({ rowData }) => {
    const onChange = (value: CheckboxValueType) => {
      rowData.selected = value as boolean
      if (onSelect) onSelect()
    }
    return h(ElCheckbox, { modelValue: rowData.selected, onChange })
  }

/*****************************/
/* Table header "select all" */
/*****************************/

type HeaderCellRendererGenerator = (tableData: Ref<any[]>, onSelect?: () => void) => HeaderCellRenderer<ManagedRow<any>>

/**
 * Takes a ref and returns a renderer bound to it
 * @param tableData the table data to bind the selection to. Will manage the row's `selected` property.
 * @returns a header renderer with a checkbox implementing the "select all" action
 */
export const selectionHeaderCellRendererGenerator: HeaderCellRendererGenerator = (tableData, onSelect) => () => {
  const _data = unref(tableData) as ManagedRow<unknown>[]
  const onChange = (value: CheckboxValueType) => {
    tableData.value = _data.map((row) => ({ ...row, selected: value }))
    if (onSelect) onSelect()
  }
  const allSelected = _data.length > 0 && _data.every((row) => row.selected)
  const containsChecked = _data.length > 0 && _data.some((row) => row.selected)
  return h(ElCheckbox, { modelValue: allSelected, indeterminate: containsChecked && !allSelected, onChange })
}
